import { Link } from "react-router-dom";
import {
  MessageSquare,
  ClipboardCheck,
  Truck,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: MessageSquare,
      title: "Get in Touch",
      description:
        "Fill out our quick form or give us a call. Tell us about your space, the number of people you serve, and any specific needs.",
    },
    {
      number: "02",
      icon: ClipboardCheck,
      title: "Custom Proposal",
      description:
        "We'll assess your space and recommend the perfect vending solution - whether that's a drink machine, combo unit, or full micro market.",
    },
    {
      number: "03",
      icon: Truck,
      title: "Installation",
      description:
        "Our team handles everything - delivery, setup, and initial stocking. Usually within 2-3 weeks of approval. You just point us to the spot.",
    },
    {
      number: "04",
      icon: Sparkles,
      title: "Enjoy & Relax",
      description:
        "We handle all ongoing service - restocking, maintenance, and product optimization based on what your users actually buy.",
    },
  ];

  const promises = [
    {
      title: "Zero Cost",
      description: "No purchase, rental, or service fees. Ever.",
    },
    {
      title: "No Maintenance",
      description: "We handle all repairs and upkeep.",
    },
    {
      title: "Smart Stocking",
      description: "Products tailored to your users' preferences.",
    },
    {
      title: "Modern Payments",
      description: "Cashless, contactless, and convenient.",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-navy relative overflow-hidden" data-testid="how-it-works-hero">
        <div className="absolute top-10 right-10 w-8 h-8 bg-gold rotate-12 opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        <div className="absolute bottom-20 left-20 w-10 h-10 bg-coral opacity-40" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
            SIMPLE PROCESS
          </span>
          <h1 className="font-heading text-4xl md:text-5xl text-cream mb-4">
            From Contact to Snacking in 4 Easy Steps
          </h1>
          <p className="text-cream/80 max-w-2xl mx-auto text-lg">
            Getting a 521 Vending machine is easier than ordering lunch. Here's
            how we make it happen.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 md:py-32" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12 md:space-y-0 md:grid md:grid-cols-4 md:gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="relative" data-testid={`step-${step.number}`}>
                {/* Connector Line (hidden on mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-gold/50" />
                )}
                
                <div className="relative z-10 flex flex-col items-center text-center">
                  {/* Number Badge */}
                  <div className="bg-coral text-cream font-mono text-sm px-3 py-1 rounded-full border-2 border-navy mb-4">
                    {step.number}
                  </div>
                  
                  {/* Icon */}
                  <div className="bg-white w-20 h-20 rounded-full border-4 border-navy flex items-center justify-center mb-6 shadow-[4px_4px_0px_0px_#F2CC8F]">
                    <step.icon className="h-10 w-10 text-navy" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-heading text-xl text-navy mb-3">
                    {step.title}
                  </h3>
                  <p className="text-navy/70 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-20 md:py-32 bg-gold/20" data-testid="promise-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-4">
              OUR PROMISE
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-navy">
              What You Get With 521 Vending
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {promises.map((promise) => (
              <div
                key={promise.title}
                className="bg-white rounded-xl border-2 border-navy p-6 text-center shadow-[4px_4px_0px_0px_#1A2A3A]"
                data-testid={`promise-${promise.title.toLowerCase().replace(" ", "-")}`}
              >
                <h3 className="font-heading text-xl text-coral mb-2">
                  {promise.title}
                </h3>
                <p className="text-navy/70 text-sm">{promise.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 md:py-32" data-testid="faq-teaser-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-retro bg-navy text-center">
            <h2 className="font-heading text-3xl text-cream mb-4">
              Still Have Questions?
            </h2>
            <p className="text-cream/80 mb-8">
              Check out our FAQ section or reach out directly. We're here to
              help!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#faq-section"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="faq-link"
              >
                View FAQ <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="bg-coral text-cream font-bold rounded-full px-8 py-4 border-2 border-cream inline-flex items-center justify-center gap-2 hover:bg-coral/90 transition-colors"
                data-testid="contact-link"
              >
                Contact Us <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-coral" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-cream/90 mb-8 max-w-xl mx-auto">
            Join hundreds of properties already enjoying hassle-free refreshment
            from 521 Vending.
          </p>
          <Link
            to="/contact"
            className="btn-secondary inline-block"
            data-testid="cta-button"
          >
            Request Your Free Machine
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
